import {GqlModel, GqlModelOperator, GqlMutationModel, GqlQueryModel} from "../graphql/graphql.model";
import {jsonProperty} from "../json.model";
import gql from "graphql-tag";
import {QuickFormArray, QuickFormGroup} from "../helpers/quick-form-group";
import {orderFormFragment, OrderFormGqlModel} from "../order/order-form.gql-model";
import {BreadcrumbGqlModel} from "../breadcrumbs/breadcrumb.gql-model";
import {SuccessResultGqlModel} from "../graphql/common.gql-model";
import {HardwareOrderDetailsInputModel} from "../order/order.gql-model";

export const bulkOrderFormKeys = [
  "name",
  "shippingDestination",
  "deliveryMethod",
  "deliveryInformation",
  "estimatedHardwareOrderDate",
  "requestedDeliveryDate",
  "sourceName",
];

const bulkHardwareOrderDataFragment: any = gql`
  fragment bulkHardwareOrderData on BulkHardwareOrder {
    id
    projectId
    workflowId
    name
    hardwareOrderDate
    estimatedHardwareOrderDate
    requestedDeliveryDate
    shippingDestination
    deliveryMethod
    deliveryInformation
    orderRequested
    orderPlaced
    projectName
    sourceName
  }
`;

export class BulkOrderGqlModel extends GqlQueryModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("string") workflowId: string;
  @jsonProperty("string") name: string;
  @jsonProperty("stringDate") hardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDate: string;
  @jsonProperty("stringDate") requestedDeliveryDate: string;
  @jsonProperty("string") shippingDestination: string;
  @jsonProperty("string") deliveryMethod: string;
  @jsonProperty("string") deliveryInformation: string;
  @jsonProperty("boolean") orderRequested: boolean;
  @jsonProperty("boolean") orderPlaced: boolean;
  @jsonProperty("string") projectName: string;
  @jsonProperty("string") sourceName: string;

  form: QuickFormGroup = new QuickFormGroup(this, bulkOrderFormKeys);
}

export class BulkOrderDataGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<BulkOrderDataGqlModel> = GqlModel.buildMapper(BulkOrderDataGqlModel);

  static query: any = gql`
    ${bulkHardwareOrderDataFragment}

    ${orderFormFragment}

    ${BreadcrumbGqlModel.fragment}

    query getHardwareOrder($id: ID!, $includeOrderForm: Boolean!, $includeBreadcrumbs: Boolean!) {
      bulkOrder: bulkHardwareOrder(id: $id) {
        ...bulkHardwareOrderData
      }

      orderForm: bulkHardwareOrderForm(id: $id) @include(if: $includeOrderForm) {
        ...orderFormData
      }

      breadcrumbs: bulkHardwareOrderBreadcrumbs(bulkOrderId: $id) @include(if: $includeBreadcrumbs) {
        ...breadcrumbData
      }
    }
  `;

  @jsonProperty(BulkOrderGqlModel) bulkOrder: BulkOrderGqlModel;
  @jsonProperty(OrderFormGqlModel) orderForm: OrderFormGqlModel;
  @jsonProperty([BreadcrumbGqlModel]) breadcrumbs: BreadcrumbGqlModel[];

  static variables(id: string, includeOrderForm: boolean = false, includeBreadcrumbs: boolean = false) {
    return {id, includeOrderForm, includeBreadcrumbs};
  }
}

export class BulkOrderListGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<BulkOrderListGqlModel> = GqlModel.buildMapper(BulkOrderListGqlModel);

  static query: any = gql`
    ${bulkHardwareOrderDataFragment}

    query bulkHardwareOrders($projectId: ID!, $search: String, $page: Int, $limit: Int) {
      rows: bulkHardwareOrders(projectId: $projectId, search: $search, page: $page, limit: $limit) {
        ...bulkHardwareOrderData
      }

      count: bulkHardwareOrderCount(projectId: $projectId, search: $search)
    }
  `;

  static variables(projectId: string, search: string, page: number, limit: number) {
    return {projectId, search, page, limit};
  }

  @jsonProperty([BulkOrderGqlModel]) rows: BulkOrderGqlModel[];
  @jsonProperty("number0") count: number;

  form: QuickFormArray = new QuickFormArray(this.rows, bulkOrderFormKeys);
}

export interface BulkOrderInputModel {
  id: string;
}

export class BulkOrderUpdateModel extends GqlModel implements BulkOrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("string") name: string;
  @jsonProperty("stringDate") hardwareOrderDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDate: string;
  @jsonProperty("stringDate") requestedDeliveryDate: string;
  @jsonProperty("string") shippingDestination: string;
  @jsonProperty("string") deliveryMethod: string;
  @jsonProperty("string") deliveryInformation: string;
  @jsonProperty("string") sourceName: string;
}

export class BulkOrderRemoveGqlModel extends GqlModel implements BulkOrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") remove: boolean;

  constructor(id: string) {
    super({id, remove: true});
  }
}

export class RequestBulkOrderPlaceGqlModel extends GqlModel implements BulkOrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") requestPlace: boolean;

  constructor(id: string) {
    super({id, requestPlace: true});
  }
}

export class ConfirmBulkOrderPlacedGqlModel extends GqlModel implements BulkOrderInputModel {
  @jsonProperty("string") id: string;
  @jsonProperty("booleanTrue") confirmPlaced: boolean;

  constructor(id: string) {
    super({id, confirmPlaced: true});
  }
}

export class BulkOrderMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<BulkOrderGqlModel> = GqlModel.buildMapper(BulkOrderGqlModel, "bulkHardwareOrder");

  static query: any = gql`
    ${bulkHardwareOrderDataFragment}

    mutation updateBulkHardwareOrder($bulkOrder: BulkHardwareOrderInput!) {
      bulkHardwareOrder(bulkOrder: $bulkOrder) {
        ...bulkHardwareOrderData
      }
    }
  `;

  @jsonProperty() bulkOrder: BulkOrderInputModel;

  constructor(bulkOrder: BulkOrderInputModel) {
    super({bulkOrder});
  }
}

export class BulkOrderDetailsMutateGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<SuccessResultGqlModel> = GqlModel.buildMapper(SuccessResultGqlModel, "result");

  static query: any = gql`
    mutation updateBulkOrderNumbersAndDates($bulkOrderId: ID!, $details: [HardwareOrderDetailsInput]) {
      result: updateBulkOrderNumbersAndDates(bulkOrderId: $bulkOrderId, details: $details) {
        success
      }
    }
  `;

  @jsonProperty("string") bulkOrderId: string;
  @jsonProperty([HardwareOrderDetailsInputModel]) details: HardwareOrderDetailsInputModel[];
}
